import { useState, useCallback } from 'react'
import { format } from 'date-fns'
import { RtwMaintenanceRequest } from '../types/return-to-work'
import { AbsenceOccurrence } from '../types/absence'
import { AbsenceDay } from '../types/absence-day'
import { DateFormats } from '../api/absence/tempTypes/generalprops'

/**
 * Custom hook for managing RTW form state
 */
export const useRtwFormState = (absenceData: AbsenceOccurrence) => {
  const [rtwData, setRtwData] = useState<RtwMaintenanceRequest>({
    absenceId: absenceData.id,
    employee: {
      employeeId: absenceData.employeeResponse.id,
      displayName: absenceData.employeeResponse.displayName,
    },
    absenceType: {
      absenceTypeId:
        absenceData.absenceDays[absenceData.absenceDays.length - 1].absenceType.absenceTypeId || 0,
      reason:
        absenceData.absenceDays[absenceData.absenceDays.length - 1].absenceType.decryptedReason ||
        '',
      reasonType:
        absenceData.absenceDays[absenceData.absenceDays.length - 1].absenceType.reasonType ||
        'Unspecified',
    },
    returnDate: format(new Date(), 'yyyy-MM-dd'),
    proceduresFollowed: false,
    proceduresComments: '',
    recurrent: false,
    recurrentComments: '',
    companyAction: false,
    companyActionComments: '',
    absenceEffects: '',
    additionalComments: '',
    missedShifts: absenceData.absenceDays.map((day: AbsenceDay) => ({
      absenceDate: format(new Date(day.date), 'yyyy-MM-dd'),
      hoursLost: day.hoursLost,
      shiftDescription: day.expectedShift.shiftString || '',
      sickHoursToBePaid: day.sickHoursToBePaid,
    })),
    signedDateTime: format(new Date(), DateFormats.DATE_AND_TIME),
  })

  const [savedRtwData, setSavedRtwData] = useState<RtwMaintenanceRequest>(rtwData)

  /**
   * Updates a single field in the RTW data
   */
  const updateField = useCallback((field: keyof RtwMaintenanceRequest, value: any) => {
    setRtwData(current => ({ ...current, [field]: value }))
  }, [])

  /**
   * Updates multiple fields at once
   */
  const updateFields = useCallback((updates: Partial<RtwMaintenanceRequest>) => {
    setRtwData(current => ({ ...current, ...updates }))
  }, [])

  /**
   * Updates saved RTW data (used when fetching existing data)
   */
  const updateSavedData = useCallback((updates: Partial<RtwMaintenanceRequest>) => {
    setSavedRtwData(current => ({ ...current, ...updates }))
    setRtwData(current => ({ ...current, ...updates }))
  }, [])

  /**
   * Resets form to initial state
   */
  const resetForm = useCallback(() => {
    setRtwData(savedRtwData)
  }, [savedRtwData])

  return {
    rtwData,
    savedRtwData,
    updateField,
    updateFields,
    updateSavedData,
    resetForm,
  }
}